import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentService } from '../services/paymentService';
import type {
  CreatePaymentIntentRequest,
  RecordManualPaymentRequest,
  InitiateRefundRequest,
  PaymentFilters,
} from '../types/payments';
import { PAYMENT_KEYS, DUES_KEYS } from './queryKeys';

// User's own payments
export const useMyPayments = (filters?: PaymentFilters) => {
  return useQuery({
    queryKey: PAYMENT_KEYS.myPayments(filters),
    queryFn: () => paymentService.getUserPayments(filters),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// All payments (admin/finance manager)
export const useAllPayments = (filters?: PaymentFilters) => {
  return useQuery({
    queryKey: PAYMENT_KEYS.allPayments(filters),
    queryFn: () => paymentService.getAllPayments(filters),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// User balance
export const useUserBalance = () => {
  return useQuery({
    queryKey: PAYMENT_KEYS.balance,
    queryFn: () => paymentService.getUserBalance(),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

// Payment methods
export const usePaymentMethods = () => {
  return useQuery({
    queryKey: PAYMENT_KEYS.methods,
    queryFn: () => paymentService.getPaymentMethods(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Create checkout session mutation
export const useCreateCheckoutSession = () => {
  return useMutation({
    mutationFn: (data: { assessmentId: string; frequency?: 'one_time' | 'monthly' | 'quarterly' | 'annually'; amount?: number }) =>
      paymentService.createCheckoutSession(data),
  });
};

// Create portal session mutation
export const useCreatePortalSession = () => {
  return useMutation({
    mutationFn: (returnUrl?: string) => paymentService.createPortalSession(returnUrl),
  });
};

// Create payment link mutation (admin)
export const useCreatePaymentLink = () => {
  return useMutation({
    mutationFn: (data: { assessmentId?: string; amount?: number; description?: string }) =>
      paymentService.createPaymentLink(data),
  });
};

// Create payment intent mutation
export const useCreatePaymentIntent = () => {
  return useMutation({
    mutationFn: (data: CreatePaymentIntentRequest) => paymentService.createPaymentIntent(data),
  });
};

// Record manual payment mutation
export const useRecordManualPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RecordManualPaymentRequest) => paymentService.recordManualPayment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.balance });
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.all });
    },
  });
};

// Send receipt email mutation
export const useSendReceipt = () => {
  return useMutation({
    mutationFn: (paymentId: string) => paymentService.sendReceipt(paymentId),
  });
};

// Initiate refund mutation
export const useInitiateRefund = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: InitiateRefundRequest }) =>
      paymentService.initiateRefund(paymentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.balance });
      queryClient.invalidateQueries({ queryKey: DUES_KEYS.all });
    },
  });
};

// Delete payment method mutation
export const useDeletePaymentMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentService.deletePaymentMethod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.methods });
    },
  });
};

// Set default payment method mutation
export const useSetDefaultPaymentMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentService.setDefaultPaymentMethod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PAYMENT_KEYS.methods });
    },
  });
};
